/**
 * Utility functions for device detection
 */

/**
 * Detect if the user is on a mobile device
 * @returns {boolean} True if mobile device, false otherwise
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined' || !window.navigator) {
    return false;
  }

  // TEST MODE: Check for testMobile parameter in URL
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('testMobile') === 'true') {
      console.log('[DeviceDetection] TEST MODE: Forcing mobile detection');
      return true;
    }
  } catch (e) {
    // Ignore URL parsing errors
  }

  const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
  
  // Check for mobile patterns in user agent
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase());
  
  // Check for touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Check screen size (mobile typically < 768px width)
  const isSmallScreen = window.innerWidth < 768;
  
  // Return true if any mobile indicator is present
  return isMobileUA || (hasTouch && isSmallScreen);
};

/**
 * Detect if the user is on iOS
 * @returns {boolean} True if iOS device, false otherwise
 */
export const isIOS = () => {
  if (typeof window === 'undefined' || !window.navigator) {
    return false;
  }
  
  // TEST MODE: Check for testIOS parameter in URL
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('testIOS') === 'true') {
      console.log('[DeviceDetection] TEST MODE: Forcing iOS detection');
      return true;
    }
  } catch (e) {
    // Ignore URL parsing errors
  }
  
  const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
  return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
};

/**
 * Detect if the user is on Android
 * @returns {boolean} True if Android device, false otherwise
 */
export const isAndroid = () => {
  if (typeof window === 'undefined' || !window.navigator) {
    return false;
  }
  
  // TEST MODE: Check for testAndroid parameter in URL
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('testAndroid') === 'true') {
      console.log('[DeviceDetection] TEST MODE: Forcing Android detection');
      return true;
    }
  } catch (e) {
    // Ignore URL parsing errors
  }
  
  const userAgent = window.navigator.userAgent || window.navigator.vendor || window.opera;
  return /android/i.test(userAgent);
};

/**
 * Redirect to app store based on device
 * @param {string} androidUrl - Google Play Store URL
 * @param {string} iosUrl - Apple App Store URL
 */
export const redirectToAppStore = (androidUrl, iosUrl) => {
  // Production: Keep minimal logging
  if (process.env.NODE_ENV === 'development') {
    console.log('[DeviceDetection] redirectToAppStore called');
    console.log('[DeviceDetection] Android URL:', androidUrl);
    console.log('[DeviceDetection] iOS URL:', iosUrl);
    console.log('[DeviceDetection] isAndroid():', isAndroid());
    console.log('[DeviceDetection] isIOS():', isIOS());
  }
  
  try {
    if (isAndroid()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[DeviceDetection] Redirecting to Android Play Store');
      }
      window.location.href = androidUrl;
    } else if (isIOS()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[DeviceDetection] Redirecting to iOS App Store');
      }
      window.location.href = iosUrl;
    } else {
      // Fallback to Android store for other devices
      if (process.env.NODE_ENV === 'development') {
        console.log('[DeviceDetection] Fallback: Redirecting to Android Play Store');
      }
      window.location.href = androidUrl;
    }
  } catch (error) {
    console.error('[DeviceDetection] Redirect error:', error);
    // Try opening in new tab as fallback
    try {
      const targetUrl = isIOS() ? iosUrl : androidUrl;
      window.open(targetUrl, '_blank');
    } catch (e) {
      console.error('[DeviceDetection] Failed to open in new tab:', e);
    }
  }
};

/**
 * App store URLs for Channelplay Jobs
 */
export const APP_STORE_URLS = {
  android: 'https://play.google.com/store/apps/details?id=com.channelplay.jobs',
  ios: 'https://apps.apple.com/app/channelplay-jobs/id6738286487'
};
